import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignHandoff,
  getHandoffConversations,
  getHandoffMessages,
  getHandoffSettings,
  getMailFailures,
  getResolvedHandoffConversations,
  resolveHandoff,
  sendAgentMessage,
  setLiveAgentsEnabled,
  takeHandoff,
} from 'services/handoff';
import type { HandoffResolvedPeriod, MailFailureFilter } from 'types/handoff';

export const HANDOFF_STATS_QUERY_KEY = ['handoff', 'stats'] as const;
export const HANDOFF_LIST_QUERY_KEY = ['handoff', 'list'] as const;
export const MAIL_FAILURES_QUERY_KEY = ['handoff', 'mail-failures'] as const;

export function handoffMessagesQueryKey(conversationId: string) {
  return ['handoff', 'messages', conversationId] as const;
}

export const HANDOFF_SETTINGS_QUERY_KEY = ['handoff', 'settings'] as const;

export function useHandoffSettingsQuery() {
  return useQuery({
    queryKey: HANDOFF_SETTINGS_QUERY_KEY,
    queryFn: getHandoffSettings,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useSetLiveAgentsEnabledMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setLiveAgentsEnabled,
    onSuccess: (data) => {
      queryClient.setQueryData(HANDOFF_SETTINGS_QUERY_KEY, data);
      void queryClient.invalidateQueries({ queryKey: HANDOFF_SETTINGS_QUERY_KEY });
    },
  });
}

export function useHandoffListQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: HANDOFF_LIST_QUERY_KEY,
    queryFn: getHandoffConversations,
    enabled: options?.enabled ?? true,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useHandoffMessagesQuery(conversationId: string | null) {
  return useQuery({
    queryKey: conversationId
      ? handoffMessagesQueryKey(conversationId)
      : ['handoff', 'messages', 'empty'],
    queryFn: () => {
      if (!conversationId) {
        return Promise.resolve([]);
      }

      return getHandoffMessages(conversationId);
    },
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? 5_000 : false,
    staleTime: 3_000,
  });
}

export function useHandoffResolvedQuery(period: HandoffResolvedPeriod, enabled: boolean) {
  return useQuery({
    queryKey: ['handoff', 'resolved', period] as const,
    queryFn: () => getResolvedHandoffConversations(period),
    enabled,
    staleTime: 10_000,
  });
}

export function useMailFailuresQuery(filter: MailFailureFilter) {
  return useQuery({
    queryKey: [...MAIL_FAILURES_QUERY_KEY, filter] as const,
    queryFn: () => getMailFailures(filter),
    staleTime: 30_000,
  });
}

export function useTakeHandoffMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: takeHandoff,
    onSuccess: async (_, conversationId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: HANDOFF_LIST_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: HANDOFF_STATS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: handoffMessagesQueryKey(conversationId) }),
      ]);
    },
  });
}

export function useAssignHandoffMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { conversationId: string; agentId: string }) =>
      assignHandoff(vars.conversationId, vars.agentId),
    onSuccess: async (_, vars) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: HANDOFF_LIST_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: HANDOFF_STATS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: handoffMessagesQueryKey(vars.conversationId) }),
      ]);
    },
  });
}

export function useResolveHandoffMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resolveHandoff,
    onSuccess: async (_, conversationId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: HANDOFF_LIST_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: HANDOFF_STATS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: MAIL_FAILURES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: handoffMessagesQueryKey(conversationId) }),
      ]);
    },
  });
}

export function useSendAgentMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendAgentMessage,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: handoffMessagesQueryKey(variables.conversationId),
        }),
        queryClient.invalidateQueries({
          queryKey: HANDOFF_LIST_QUERY_KEY,
        }),
      ]);
    },
  });
}
